/**
 * 会員登録リクエストパラメータを保持するコンテキスト。
 */
import { createContext, useContext } from '@berlin-front/common/composables/context';
import { reactive, computed, ref, shallowReactive } from '@vue/composition-api';
import { ModalBridgeContext } from '@services/ModalBridgeContext';
import { event } from 'vue-gtag';
import { prefectureCodeList } from '@/entity/prefectureCodeList';
import { getToDayJp, isValidDate, toHankaku, toHankakuNumber, toZenkaku } from '@berlin-front/common/utils';
import { isValidKanaString } from '@berlin-front/common/composables/validator';

export const MemberRequestContext = createContext(() => {
  const bridge = useContext(ModalBridgeContext);

  const initialBirthday: string = bridge.registerFormDefault?.birthday || '';
  const birthdayArray =
    isValidDate(initialBirthday) &&
    Date.parse(`${getToDayJp()}T00:00:00Z`) >= Date.parse(`${initialBirthday}T00:00:00Z`) &&
    Date.parse(`${initialBirthday}T00:00:00Z`) >= Date.parse('1900-01-01T00:00:00Z')
      ? initialBirthday.split('-')
      : ['', '', ''];

  const birthdayYear = birthdayArray[0];
  const birthdayMonth = birthdayArray[1] ? birthdayArray[1].padStart(2, '0') : birthdayArray[1];
  const birthdayDay = birthdayArray[2] ? birthdayArray[2].replace(/^0+/, '') : birthdayArray[2];

  const defaultRegisterCustomerName = computed(() => {
    const familyName = bridge.registerFormDefault?.customerFamilyName ?? '';
    const givenName = bridge.registerFormDefault?.customerGivenName ?? '';
    return isValidKanaString(`${familyName}${givenName}`)
      ? {
          // 氏名がカナのみからなる場合、プリセットしない
          familyName: '',
          givenName: '',
        }
      : {
          // 氏名にカナ以外が含まれてれば、プリセットする。
          familyName,
          givenName,
        };
  });

  const registerMemberFormState = reactive({
    mailAddress: bridge.registerFormDefault?.email || '',
    familyName: defaultRegisterCustomerName.value.familyName,
    givenName: defaultRegisterCustomerName.value.givenName,
    familyNameKana: bridge.registerFormDefault?.customerFamilyNameKana || '',
    givenNameKana: bridge.registerFormDefault?.customerGivenNameKana || '',
    // 画面上でundefinedの場合は2で埋めなおされる。だからあまり意味ないけどundefined = 2 としておくことにする。
    sexDivision: bridge.registerFormDefault?.sexDivision || (undefined as unknown as '2'),
    birthdayYear,
    birthdayMonth,
    birthdayDay,
    zip: bridge.registerFormDefault?.zipCode || '',
    prefectureCode: '',
    address2First: '',
    address2Second: (bridge.registerFormDefault?.zipCode && bridge.registerFormDefault?.address) || '',
    address3: '',
    phoneNumber: bridge.registerFormDefault?.phoneNumber || '',
    password: '',
  });

  // GA4 プリセットイベント送信
  if (
    // bridge.registerFormDefault?.email ||
    bridge.registerFormDefault?.customerFamilyName ||
    bridge.registerFormDefault?.customerGivenName ||
    bridge.registerFormDefault?.customerFamilyNameKana ||
    bridge.registerFormDefault?.customerGivenNameKana ||
    bridge.registerFormDefault?.sexDivision ||
    bridge.registerFormDefault?.zipCode ||
    (bridge.registerFormDefault?.zipCode && bridge.registerFormDefault?.address) ||
    bridge.registerFormDefault?.phoneNumber
  ) {
    event('preset', {
      preset_any_member_data: 'yes',
    });
  } else {
    event('preset', {
      preset_any_member_data: 'no',
    });
  }

  const { userNo } = bridge;
  const normalize = () => {
    Object.assign(registerMemberFormState, {
      // 国際化ドメイン入力に対応しない限り、メールアドレスにはascii文字しか使用されない仕様であるため、半角に変更する。
      mailAddress: toHankaku(registerMemberFormState.mailAddress),
      familyName: toZenkaku(registerMemberFormState.familyName),
      givenName: toZenkaku(registerMemberFormState.givenName),
      zip: toHankakuNumber(registerMemberFormState.zip),
      address2First: toZenkaku(registerMemberFormState.address2First ?? ''),
      address2Second: toZenkaku(registerMemberFormState.address2Second ?? ''),
      address3: toZenkaku(registerMemberFormState.address3),
      familyNameKana: toZenkaku(registerMemberFormState.familyNameKana),
      givenNameKana: toZenkaku(registerMemberFormState.givenNameKana),
      phoneNumber: toHankakuNumber(registerMemberFormState.phoneNumber),
      birthdayYear: toHankakuNumber(registerMemberFormState.birthdayYear),
      birthdayDay: toHankakuNumber(registerMemberFormState.birthdayDay),
    });
  };
  normalize();

  const parentalConsentCheck = ref(false);
  const termsConsentCheck = ref(false);
  /** 登録フォームでのパスワード、Emailの編集可否 */
  const isEmailEditable = ref(true);

  const memberRegistrationRoute = ref(7);

  /** zipコード上3桁 */
  const zipF3 = computed(() => registerMemberFormState.zip.substring(0, 3));
  /** zipコード下4桁 */
  const zipB4 = computed(() => registerMemberFormState.zip.substring(3));
  /** 市町村番地 */
  const address2 = computed(() => registerMemberFormState.address2First + registerMemberFormState.address2Second);
  /** 生年月日 */
  const birthday = computed(() =>
    [
      registerMemberFormState.birthdayYear,
      registerMemberFormState.birthdayMonth,
      registerMemberFormState.birthdayDay.padStart(2, '0'),
    ].join('-'),
  );
  /** 都道府県名 */
  const prefectureName = computed(
    () =>
      prefectureCodeList.find((prefecture) => prefecture.code === registerMemberFormState.prefectureCode)?.name || '',
  );

  const SEX_DIVISION = ['2', '1'] as const;
  const sexDivisionRender = (v: string | number | boolean): string =>
    ({
      '1': '男性',
      '2': '女性',
      '3': 'その他',
    })[typeof v === 'boolean' ? 3 : v] || 'その他';

  const MONTH_LIST = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'] as const;
  const monthToString = (v: string | number): string =>
    ({
      '01': '1月',
      '02': '2月',
      '03': '3月',
      '04': '4月',
      '05': '5月',
      '06': '6月',
      '07': '7月',
      '08': '8月',
      '09': '9月',
      '10': '10月',
      '11': '11月',
      '12': '12月',
    })[v]!;

  // shallowReactiveにすると、ネストした階層のreactive要素の再編成が行われなくなる。
  // 通常、reactiveのプロパティにrefが存在する場合、refは展開されてしまうが、展開させたくないためshallowReactiveで囲っている。
  return shallowReactive({
    // 状態
    parentalConsentCheck,
    termsConsentCheck,
    registerMemberFormState,
    isEmailEditable,
    memberRegistrationRoute,
    userNo,
    // computed
    zipF3,
    zipB4,
    address2,
    birthday,
    prefectureName,
    // データ定義とマッパー
    SEX_DIVISION,
    sexDivisionRender,
    MONTH_LIST,
    monthToString,

    normalize,
  });
}, 'MemberRequestContext');
